import  { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  Paper,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Pagination,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Autocomplete,
  Snackbar,
} from "@mui/material";
import {
  Search,
  Settings,
  Phone,
  Email,
  Person,
  BusinessCenter,
  VerifiedUser,
  PersonAdd,
  Delete,
  People,
} from "@mui/icons-material";
import Layout from "../components/layout/AdminLayout";
import config from "../config.js";

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [openAccessDialog, setOpenAccessDialog] = useState(false);
  const [openClientAssignDialog, setOpenClientAssignDialog] = useState(false);
  const [filterRole, setFilterRole] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [permissions, setPermissions] = useState({
    read: false,
    write: false,
    update: false,
    delete: false,
  });
  const [selectedClientToAssign, setSelectedClientToAssign] = useState(null);
  const { API_BASE_URL } = config;

  // Toast state
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const closeToast = () => {
    setToast({
      ...toast,
      open: false,
    });
  };

  const showToast = (message, severity = "success") => {
    setToast({
      open: true,
      message,
      severity,
    });
  };

  const fetchUsers = useCallback( async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/users?page=${page}&limit=9&role=${
          filterRole === "all" ? "" : filterRole
        }&search=${searchQuery}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setUsers(response.data.data.users);
      setTotalPages(response.data.data.pagination.totalPages);
      setError(null);

      // Fetch stats
      const statsResponse = await axios.get(
        `${API_BASE_URL}/api/v1/users/stats`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setStats(statsResponse.data.data);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch users.");
      showToast(
        "Failed to fetch users: " +
          (err.response?.data?.message || "Unknown error"),
        "error"
      );
      console.error("Error fetching users:", err);
    } finally {
      setLoading(false);
    }
  },[API_BASE_URL, page, filterRole, searchQuery]);

  const fetchClients = useCallback (async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/users?role=user&limit=100`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setClients(
        response.data.data.users.filter((client) => client.role === "user")
      );
    } catch (err) {
      console.error("Error fetching clients:", err);
      showToast(
        "Failed to fetch clients: " +
          (err.response?.data?.message || "Unknown error"),
        "error"
      );
    } finally {
      setLoading(false);
    }
  },[API_BASE_URL]);

  const updateUserAccess = async (userId, newAccess) => {
    try {
      setLoading(true);
      await axios.patch(
        `${API_BASE_URL}/api/v1/users/${userId}/access`,
        {
          role: newAccess.role,
          permissions: newAccess.permissions,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      fetchUsers();
      setOpenAccessDialog(false);
      setError(null);
      showToast(
        `User access successfully updated for ${selectedUser.username}`
      );
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update user access.");
      showToast(
        "Failed to update user access: " +
          (err.response?.data?.message || "Unknown error"),
        "error"
      );
      console.error("Error updating access:", err);
    } finally {
      setLoading(false);
    }
  };

  const assignClientToProfessional = async () => {
    if (!selectedUser || !selectedClientToAssign) return;

    try {
      setLoading(true);
      await axios.post(
        `${API_BASE_URL}/api/v1/users/${selectedUser._id}/clients`,
        {
          clientId: selectedClientToAssign._id,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const response = await axios.get(
        `${API_BASE_URL}/api/v1/users/${selectedUser._id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setSelectedUser(response.data.data.user);
      setSelectedClientToAssign(null);
      setError(null);
      showToast(
        `Client ${selectedClientToAssign.username} successfully assigned to ${selectedUser.username}`
      );
    } catch (err) {
      setError(err.response?.data?.message || "Failed to assign client.");
      showToast(
        "Failed to assign client: " +
          (err.response?.data?.message || "Unknown error"),
        "error"
      );
      console.error("Error assigning client:", err);
    } finally {
      setLoading(false);
    }
  };

  const removeClientFromProfessional = async (clientId) => {
    try {
      setLoading(true);
      // Find client name before removing
      const clientToRemove = selectedUser.clients.find(
        (client) => client._id === clientId
      );
      const clientName = clientToRemove ? clientToRemove.username : "Client";

      await axios.delete(
        `${API_BASE_URL}/api/v1/users/${selectedUser._id}/clients/${clientId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      // Refresh user data to show updated client list
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/users/${selectedUser._id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setSelectedUser(response.data.data.user);
      setError(null);
      showToast(
        `${clientName} successfully removed from ${selectedUser.username}`
      );
    } catch (err) {
      setError(err.response?.data?.message || "Failed to remove client.");
      showToast(
        "Failed to remove client: " +
          (err.response?.data?.message || "Unknown error"),
        "error"
      );
      console.error("Error removing client:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchClients();
  }, [page, filterRole, searchQuery,fetchClients,fetchUsers]);

  useEffect(() => {
    if (selectedUser && selectedUser.permissions) {
      setPermissions(selectedUser.permissions);
    } else {
      setPermissions({
        read: false,
        write: false,
        update: false,
        delete: false,
      });
    }
  }, [selectedUser]);

  const handleUserSelect = (user) => {
    setSelectedUser({
      ...user,
      permissions: user.permissions || {
        read: false,
        write: false,
        update: false,
        delete: false,
      },
    });
    setOpenAccessDialog(true);
  };

  const handleClientAssignment = async (user) => {
    setSelectedUser(user);
    setOpenClientAssignDialog(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/users/${user._id}/with-clients`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const userData = response.data.data.user;
      const clientsData = response.data.data.clients || [];

      setSelectedUser({
        ...userData,
        clients: clientsData,
      });
    } catch (err) {
      console.error("Error fetching user with clients:", err);
      showToast(
        "Error fetching user client data: " +
          (err.response?.data?.message || "Unknown error"),
        "error"
      );
    }
    await fetchClients();
  };

  const handlePermissionChange = (permission) => {
    setPermissions({
      ...permissions,
      [permission]: !permissions[permission],
    });
  };

  const getAccessStatusChip = (role) => {
    switch (role) {
      case "ca":
        return <Chip label="CA Access" color="primary" size="small" />;
      case "cs":
        return <Chip label="CS Access" color="secondary" size="small" />;
      case "admin":
        return <Chip label="Admin" color="error" size="small" />;
      case "employee":
        return <Chip label="Employee" color="info" size="small" />;
      case "user":
        return <Chip label="User" color="default" size="small" />;
      default:
        return <Chip label="User" color="default" size="small" />;
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case "ca":
        return <BusinessCenter fontSize="small" color="primary" />;
      case "cs":
        return <BusinessCenter fontSize="small" color="secondary" />;
      case "admin":
        return <VerifiedUser fontSize="small" color="error" />;
      case "employee":
        return <Person fontSize="small" color="info" />;
      case "user":
        return <Person fontSize="small" color="action" />;
      default:
        return <Person fontSize="small" color="action" />;
    }
  };

  const isProfessionalRole = (role) => {
    return ["ca", "cs", "admin", "employee"].includes(role);
  };

  return (
    <Layout>
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: 3 }}>
        <Box sx={{ maxWidth: 1200, mx: "auto", px: 3 }}>
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: "bold", mb: 1 }}
            >
              Admin Access Management
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Manage user roles, permissions, and client assignments
            </Typography>
          </Box>

          {/* Toast Notification */}
          <Snackbar
            open={toast.open}
            autoHideDuration={6000}
            onClose={closeToast}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert
              onClose={closeToast}
              severity={toast.severity}
              sx={{ width: "100%" }}
              variant="filled"
            >
              {toast.message}
            </Alert>
          </Snackbar>

          {/* Stats Cards */}
          {stats && (
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">Total Users</Typography>
                    <Typography variant="h4">{stats.totalUsers}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              {stats.roleDistribution.map((stat) => (
                <Grid item xs={12} md={3} key={stat.role}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6">
                        {stat.role ? stat.role.toUpperCase() : "Regular Users"}
                      </Typography>
                      <Typography variant="h4">{stat.count}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Paper sx={{ p: 2, mb: 4 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search users by name, email, or phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <Search sx={{ color: "text.secondary", mr: 1 }} />
                    ),
                  }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filter by Role</InputLabel>
                  <Select
                    value={filterRole}
                    label="Filter by Role"
                    onChange={(e) => {
                      setFilterRole(e.target.value);
                      setPage(1);
                    }}
                  >
                    <MenuItem value="all">All Users</MenuItem>
                    <MenuItem value="ca">CA Access</MenuItem>
                    <MenuItem value="cs">CS Access</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="employee">Employee</MenuItem>
                    <MenuItem value="user">User</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          <div>
            {loading && users.length === 0 ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Grid container spacing={3}>
                  {users.map((user) => (
                    <Grid item xs={12} md={6} lg={4} key={user._id}>
                      <Card sx={{ position: "relative" }}>
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 2,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              {getRoleIcon(user.role)}
                              <Typography variant="h6">
                                {user.username}
                              </Typography>
                            </Box>
                            {getAccessStatusChip(user.role)}
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <Email fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {user.email}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <Phone fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {user.phone || "No phone number"}
                            </Typography>
                          </Box>

                          {user.permissions && (
                            <Box sx={{ mt: 1, mb: 1 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Permissions:
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 1,
                                  mt: 0.5,
                                }}
                              >
                                {user.permissions.read && (
                                  <Chip
                                    label="Read"
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                                {user.permissions.write && (
                                  <Chip
                                    label="Write"
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                                {user.permissions.update && (
                                  <Chip
                                    label="Update"
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                                {user.permissions.delete && (
                                  <Chip
                                    label="Delete"
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                                {!user.permissions.read &&
                                  !user.permissions.write &&
                                  !user.permissions.update &&
                                  !user.permissions.delete && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      No permissions assigned
                                    </Typography>
                                  )}
                              </Box>
                            </Box>
                          )}

                          {user.clients && user.clients.length > 0 && (
                            <Box sx={{ mt: 1, mb: 1 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Assigned Clients: {user.clients.length}
                              </Typography>
                            </Box>
                          )}

                          <Divider sx={{ my: 2 }} />
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Button
                              startIcon={<Settings />}
                              onClick={() => handleUserSelect(user)}
                              color="secondary"
                              variant="contained"
                              size="small"
                            >
                              Modify Access
                            </Button>

                            {isProfessionalRole(user.role) && (
                              <Button
                                startIcon={<People />}
                                onClick={() => handleClientAssignment(user)}
                                color="info"
                                variant="outlined"
                                size="small"
                              >
                                Assign Clients
                              </Button>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {users.length === 0 && !loading && (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No users found matching your criteria
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                  />
                </Box>
              </>
            )}
          </div>

          {/* Role and Permissions Dialog */}
          <Dialog
            open={openAccessDialog}
            onClose={() => setOpenAccessDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Modify Access</DialogTitle>
            <DialogContent>
              <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                <Tabs
                  value={activeTab}
                  onChange={(e, newValue) => setActiveTab(newValue)}
                >
                  <Tab label="Role Assignment" />
                  <Tab label="Access Permissions" />
                </Tabs>
              </Box>

              <Box sx={{ py: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  {selectedUser?.username}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {selectedUser?.email}
                </Typography>

                {activeTab === 0 && (
                  <Box sx={{ mt: 3 }}>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Role</InputLabel>
                      <Select
                        value={selectedUser?.role || "user"}
                        label="Role"
                        onChange={(e) =>
                          setSelectedUser({
                            ...selectedUser,
                            role: e.target.value,
                          })
                        }
                      >
                        <MenuItem value="user">User</MenuItem>
                        <MenuItem value="ca">CA Access</MenuItem>
                        <MenuItem value="cs">CS Access</MenuItem>
                        {/* <MenuItem value="admin">Admin</MenuItem>      */}
                        <MenuItem value="employee">Employee</MenuItem>
                      </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary">
                      Note: Changing role may affect what files and clients a
                      user can access
                    </Typography>
                  </Box>
                )}

                {activeTab === 1 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Grant Specific Permissions
                    </Typography>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={permissions.read}
                            onChange={() => handlePermissionChange("read")}
                          />
                        }
                        label="Read Access (View client files and information)"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={permissions.write}
                            onChange={() => handlePermissionChange("write")}
                          />
                        }
                        label="Write Access (Create new documents and files)"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={permissions.update}
                            onChange={() => handlePermissionChange("update")}
                          />
                        }
                        label="Update Access (Modify existing documents)"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={permissions.delete}
                            onChange={() => handlePermissionChange("delete")}
                          />
                        }
                        label="Delete Access (Remove documents and files)"
                      />
                    </FormGroup>
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenAccessDialog(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={() =>
                  updateUserAccess(selectedUser?._id, {
                    role: selectedUser?.role,
                    permissions: permissions,
                  })
                }
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : "Update Access"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Client Assignment Dialog */}
          <Dialog
            open={openClientAssignDialog}
            onClose={() => setOpenClientAssignDialog(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              Manage Client Assignments for {selectedUser?.username}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                {/* Current Client List */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Currently Assigned Clients
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {selectedUser?.clients && selectedUser.clients.length > 0 ? (
                    <List>
                      {selectedUser.clients.map((client) => (
                        <ListItem
                          key={client._id}
                          secondaryAction={
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              onClick={() =>
                                removeClientFromProfessional(client._id)
                              }
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          }
                        >
                          <ListItemAvatar>
                            <Avatar>
                              <Person />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={client.username}
                            secondary={client.email}
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Box sx={{ textAlign: "center", py: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        No clients assigned yet
                      </Typography>
                    </Box>
                  )}
                </Grid>

                {/* Add Client */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Assign New Client
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Autocomplete
                    id="client-select"
                    options={clients.filter(
                      (client) =>
                        !selectedUser?.clients?.some(
                          (c) => c._id === client._id
                        )
                    )}
                    getOptionLabel={(option) =>
                      `${option.username} (${option.email})`
                    }
                    value={selectedClientToAssign}
                    onChange={(event, newValue) => {
                      setSelectedClientToAssign(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search for clients"
                        variant="outlined"
                        fullWidth
                        helperText="Search by name or email"
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <Person color="action" sx={{ mr: 1 }} />
                          <Box>
                            <Typography variant="body1">
                              {option.username}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {option.email}
                            </Typography>
                          </Box>
                        </Box>
                      </li>
                    )}
                    fullWidth
                    sx={{ mb: 3 }}
                  />

                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PersonAdd />}
                    onClick={assignClientToProfessional}
                    disabled={!selectedClientToAssign || loading}
                    fullWidth
                  >
                    {loading ? <CircularProgress size={24} /> : "Assign Client"}
                  </Button>

                  <Box sx={{ mt: 3 }}>
                    <Typography variant="caption" color="text.secondary">
                      Note: When you assign a client to this{" "}
                      {selectedUser?.role}, they will be able to access all
                      client files and data based on their granted permissions.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenClientAssignDialog(false)}>
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </Layout>
  );
};

export default AdminDashboard;
